/**
 * @file useOrderCard.ts
 * @description Данные и переходы карточки заказа (вынесено из
 * `OrderDetailPage` в phase19.24 этап 1, шаг 5.3; phase19.31 — тихое
 * перечитывание).
 *
 * Индикатор на всю страницу — ТОЛЬКО при первом открытии, пока заказа ещё
 * нет (`loading`). Любое перечитывание после действия во вкладке —
 * «Отгрузки», «Оплаты и документы», «Возвраты» — тихое: заказ остаётся на
 * экране и обновляется на месте, а страница показывает тонкий признак
 * `refreshing`. Раньше `refresh()` ставил `loading`, страница заменяла всю
 * карточку спиннером — вкладка размонтировалась, открытые шторки и прокрутка
 * пропадали («нижняя часть заказа постоянно перегружает страницу»).
 *
 * Ошибка перечитывания карточку не стирает — человек видит прежние данные и
 * сообщение. Заявку F30 запрашиваем всегда: она живёт и после перехода в
 * сборку; её недоступность карточку не роняет.
 *
 * Ответы устаревших запросов отбрасываются (номер запроса `reqRef`): при
 * переходе с заказа A на B поздний ответ A не подменяет B. Смена `id`
 * сбрасывает прежний заказ сразу, до ответа.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { useNotify } from "../components/ui/NotifyProvider";
import { ApiError } from "../lib/api-client";
import { getDeliveryAgreement, type DeliveryAgreementDto } from "../lib/api-delivery-agreement";
import { getOrder, orderAction, type OrderDto } from "../lib/api-orders";
import {
  ORDER_STAGE_TRANSITIONS,
  isSideAction,
  isStageTransitionAction,
  type OrderActionKey,
} from "../lib/order-actions";
import { transitionOrderToKind } from "../lib/order-stage-transition";

export interface OrderCardState {
  order: OrderDto | null;
  agreement: DeliveryAgreementDto | null;
  /** Первая загрузка: заказа ещё нет на экране. */
  loading: boolean;
  /** Тихое перечитывание: заказ на экране, данные обновляются на месте. */
  refreshing: boolean;
  /** Ошибка первой загрузки (заказа на экране нет). */
  error: string | null;
  /** Перечитать заказ; после первой загрузки — всегда тихо. */
  refresh: () => Promise<void>;
  /** То же, что `refresh` (старое имя для вкладок-редакторов). */
  refreshQuiet: () => Promise<void>;
  acting: boolean;
  /**
   * Выполнить действие карточки. Переходы без своего эндпоинта («Выдать
   * заказ») уходят в `POST /transition`, действия рядом с заказом
   * (`requestReview`) обрабатывает страница — здесь они игнорируются.
   */
  runAction: (action: OrderActionKey) => Promise<void>;
  /** «Отменить» с причиной из окна отмены; `true` — заказ отменён. */
  cancel: (reason: string) => Promise<boolean>;
}

/** Человеческий текст ошибки сервера. */
function errorText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

/**
 * @param onChanged перечитать всё, что зависит от заказа, но живёт отдельным
 * запросом: «Путь заказа» (`useOrderChain`). Без него шапка после главной
 * кнопки менялась, а степпер и узлы — только после F5 (дефект QA 2026-09-21).
 */
export function useOrderCard(id: string | undefined, onChanged?: () => void): OrderCardState {
  const notify = useNotify();
  // Ссылки, а не зависимости: смена объекта уведомлений не должна
  // перезапускать загрузку заказа.
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const [order, setOrder] = useState<OrderDto | null>(null);
  const [agreement, setAgreement] = useState<DeliveryAgreementDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [acting, setActing] = useState(false);
  /** Заказ уже показан — дальше перечитываем тихо. */
  const shown = useRef(false);
  /**
   * Номер последнего запроса. Ответ прежнего (перешли с заказа A на B, а
   * ответ A пришёл позже) отбрасываем: иначе на экране оказался бы A, и
   * «Отменить» сработало бы по нему.
   */
  const reqRef = useRef(0);
  /** Текущий заказ страницы — для ответов действий, пришедших после перехода. */
  const idRef = useRef(id);
  idRef.current = id;

  const load = useCallback(async () => {
    if (!id) return;
    const req = ++reqRef.current;
    const quiet = shown.current;
    if (quiet) setRefreshing(true);
    try {
      const next = await getOrder(id);
      if (req !== reqRef.current) return;
      shown.current = true;
      setOrder(next);
      setError(null);
    } catch (err) {
      if (req !== reqRef.current) return;
      const text = errorText(err, "Не удалось загрузить заказ");
      // Заказ на экране — оставляем его и сообщаем; иначе — ошибка вместо карточки.
      if (quiet) notifyRef.current.error(`Не удалось обновить заказ: ${text}`);
      else setError(text);
    } finally {
      if (quiet && req === reqRef.current) setRefreshing(false);
    }
    if (req !== reqRef.current) return;
    let nextAgreement: DeliveryAgreementDto | null = null;
    try {
      nextAgreement = await getDeliveryAgreement(id);
    } catch {
      nextAgreement = null;
    }
    if (req === reqRef.current) setAgreement(nextAgreement);
  }, [id]);

  // Другой заказ — снова первая загрузка (индикатор на всю страницу):
  // прежний заказ, заявка и признаки сбрасываются сразу, не дожидаясь ответа.
  useEffect(() => {
    shown.current = false;
    setOrder(null);
    setAgreement(null);
    setError(null);
    setRefreshing(false);
    void load();
  }, [load]);

  const runAction = useCallback(
    async (action: OrderActionKey) => {
      if (!order || action === "cancel" || isSideAction(action)) return;
      const startedFor = idRef.current;
      setActing(true);
      try {
        const next = isStageTransitionAction(action)
          ? await transitionOrderToKind(order, ORDER_STAGE_TRANSITIONS[action])
          : await orderAction[action](order.id);
        // Ответ пришёл, когда страница уже показывает другой заказ, — не применяем.
        if (idRef.current !== startedFor) return;
        setOrder(next);
        notify.success("Действие выполнено");
        // Стадия сменилась — «Путь заказа» обязан пересобраться здесь же.
        onChanged?.();
      } catch (err) {
        // Любая ошибка перехода (в т.ч. 409 invalid transition) показывается
        // текстом сервера: молчаливый отказ читается как «кнопка не работает».
        notify.error(errorText(err, "Не удалось выполнить действие"));
      } finally {
        setActing(false);
      }
    },
    [notify, onChanged, order],
  );

  const cancel = useCallback(
    async (reason: string): Promise<boolean> => {
      const text = reason.trim();
      if (!order || !text) return false;
      const startedFor = idRef.current;
      setActing(true);
      try {
        const next = await orderAction.cancel(order.id, text);
        if (idRef.current === startedFor) setOrder(next);
        notify.success("Заказ отменён");
        onChanged?.();
        return true;
      } catch (err) {
        notify.error(errorText(err, "Не удалось отменить заказ"));
        return false;
      } finally {
        setActing(false);
      }
    },
    [notify, onChanged, order],
  );

  return {
    order,
    agreement,
    loading: order === null && error === null && !!id,
    refreshing,
    error,
    refresh: load,
    refreshQuiet: load,
    acting,
    runAction,
    cancel,
  };
}
